<template>
    <Page v-if="isOrganizationManager" class="stek-management-page">
        <template #eyebrow>
            <Eyebrow icon="pi pi-cog">Management</Eyebrow>
        </template>
        <template #title>
            <h1 class="page-title">Stek Management</h1>
        </template>

        <Section class="import-section">
            <template #title>Import Stek</template>
            <div class="import-controls">
                <p class="description">
                    Upload a stek export ZIP file to import it into your
                    organization. This will restore all domains, products,
                    processes, and git history.
                </p>
                <Button
                    label="Import Stek"
                    icon="pi pi-upload"
                    @click="openImportDialog"
                />
            </div>
        </Section>

        <Section class="steks-list-section">
            <template #title>Manage Existing Steks</template>
            <DataTable :value="steks" :loading="isLoading" class="steks-table">
                <Column field="name" header="Name" sortable></Column>
                <Column field="fqdn" header="Domain" sortable>
                    <template #body="slotProps">
                        {{ slotProps.data.fqdn || "-" }}
                    </template>
                </Column>
                <Column header="Actions" class="actions-column">
                    <template #body="slotProps">
                        <div class="action-buttons">
                            <Button
                                icon="pi pi-download"
                                label="Export"
                                text
                                size="small"
                                @click="exportStek(slotProps.data)"
                                :loading="
                                    exportingId === slotProps.data.identity
                                "
                            />
                            <Button
                                icon="pi pi-external-link"
                                label="Open"
                                text
                                size="small"
                                @click="navigateToStek(slotProps.data)"
                            />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import { computed, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";

import Eyebrow from "@/components/Eyebrow.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import ImportStekDialog from "@/components/dialogs/ImportStekDialog.vue";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { Stek, useSteksManager } from "@/stores/steks-store";
import { useUserManager } from "@/stores/user-store";

const { steks, isLoading } = useSteksManager();
const { organization } = useOrganizationManager();
const { currentOrganizationPermissions } = useUserManager();
const { getToken } = useAuthenticationStore();
const toast = useToast();
const router = useRouter();
const dialog = useDialog();

const isOrganizationManager = computed(() => {
    return currentOrganizationPermissions.value?.can_manage_steks ?? false;
});

watchEffect(() => {
    if (
        !isLoading.value &&
        organization.value &&
        !isOrganizationManager.value
    ) {
        toast.add({
            severity: "error",
            summary: "Access Denied",
            detail: "You do not have permission to access this page.",
            life: 3000,
        });
        router.push({
            name: "organizationHome",
            params: { orgName: organization.value.name },
        });
    }
});

const exportingId = ref<string | null>(null);

const openImportDialog = () => {
    dialog.open(ImportStekDialog, {
        props: {
            header: "Import Stek",
            style: { maxWidth: "500px", width: "90%" },
            modal: true,
            maximizable: false,
            draggable: false,
            closable: true,
        },
    });
};

const exportStek = async (stek: Stek) => {
    if (!organization.value) return;

    try {
        exportingId.value = stek.identity;
        const token = await getToken();
        const baseUrl = import.meta.env.VITE_API_URL;

        const response = await fetch(
            `${baseUrl}/stek/stek_mgt/export_stek/start`,
            {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Organization": organization.value.identity,
                    "Stek": stek.identity,
                },
            }
        );

        if (!response.ok) {
            throw new Error(
                `Failed to export stek: ${response.status} ${response.statusText}`
            );
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `stek_export_${stek.name}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.add({
            severity: "success",
            summary: "Export Successful",
            detail: `Stek ${stek.name} has been exported.`,
            life: 3000,
        });
    } catch (error) {
        console.error("Export failed:", error);
        toast.add({
            severity: "error",
            summary: "Export Failed",
            detail: error instanceof Error ? error.message : "Unknown error",
            life: 5000,
        });
    } finally {
        exportingId.value = null;
    }
};

const navigateToStek = (stek: Stek) => {
    if (organization.value?.name) {
        router.push({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stek.name,
            },
        });
    }
};
</script>

<style scoped lang="scss">
.stek-management-page {
    max-width: 1000px;
}

.page-title {
    margin: 0;
    font-size: var(--text-xl);
}

.import-section {
    .import-controls {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: flex-start;

        .description {
            color: var(--text-color-secondary);
            margin: 0;
            max-width: 600px;
        }
    }
}

.steks-table {
    margin-top: 1rem;
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
}

.actions-column {
    width: 250px;
}
</style>
